/**
 * Balloon Generator — a build course with a live calculator.
 *
 * The machine: a lifting-gas balloon on a long rope. As it rises it unwinds a
 * drum that drives, through step-up gearing, a DC motor used as a generator.
 * Buoyancy does the work on the up-stroke; a person cranks the balloon back
 * down to reset. Nothing here creates energy — buoyancy sets the FORCE ceiling
 * and gearing only trades force for speed. Every number below is the honest
 * consequence of that.
 *
 * Structure: six lessons, in build order, each ending in the numbers that
 * lesson produces. One shared input state feeds all of them — change a value in
 * any lesson and the whole chain downstream of it moves. This replaces the old
 * two-tab / two-solver page, which computed the same physics twice and buried
 * the teaching under an explorer.
 *
 * Runs entirely in the browser — works offline once loaded. Strings are
 * hardcoded English, consistent with the other open-source tool pages.
 */

import React, { useMemo, useState } from 'react';
import SEO from '../../../components/SEO';

const G = 9.81; // m/s^2
const RHO_AIR = 1.225; // kg/m^3 at 15 C, sea level

const SITE_URL = 'https://rivieratech.solutions';
const TOOL_PATH = '/open-source/solutions/balloon-generator';

/* Field imagery. Generated with scripts/generate-page-assets.js into public/,
 * so a missing file degrades to a hidden figure instead of breaking the build
 * the way a bare `import` of an ungenerated asset would. */
const IMG = {
  hero: '/assets/balloon/hero-field.webp',
  schematic: '/assets/balloon/schematic.webp',
  house: '/assets/balloon/house-install.webp',
  drum: '/assets/balloon/drum-gearbox.webp',
  lift: '/assets/balloon/lift-envelope.webp',
  reset: '/assets/balloon/reset-crank.webp',
  cluster: '/assets/balloon/cluster-commons.webp',
  bench: '/assets/balloon/bench-electronics.webp',
};

const GASES = {
  helium: {
    label: 'Helium',
    density: 0.1785,
    note: 'Safe, inert, easy to buy. ~1.05 kg of lift per m³.',
    warn: false,
  },
  hydrogen: {
    label: 'Hydrogen',
    density: 0.0899,
    note: 'About 8% more lift and cheap to make from water — but flammable. Keep it away from sparks and flame.',
    warn: true,
  },
  hotair: {
    label: 'Hot air (~100 °C)',
    density: 0.946,
    note: 'No gas to buy, but only ~0.28 kg of lift per m³ — needs a huge envelope and constant heat.',
    warn: false,
  },
};

/**
 * Generators worth actually buying, and what they cost you.
 *
 * `phases: 3` means a brushless machine — real AC, so it needs a rectifier, and
 * two diodes are always in series across the bridge. Schottky keeps that to
 * ~0.6 V total; silicon costs ~1.4 V, which is most of your output on a slow
 * rise. Brushed machines skip the bridge but lose a similar amount across the
 * brushes at low current — modelled here as a drop on the brushless side only,
 * because the brush drop is load-dependent and not worth faking precisely.
 */
const GENERATORS = {
  brushed775: {
    label: 'Cheap brushed (RS-775 class)',
    Kv: 460, Ri: 0.35, phases: 1, drop: 0,
    note: 'Cheapest way to start. High Kv means it needs real speed before it makes volts — fine for fast rises, hopeless for slow ones.',
  },
  gearmotor: {
    label: 'Brushed planetary gearmotor (low Kv)',
    Kv: 60, Ri: 3.5, phases: 1, drop: 0,
    note: 'The built-in gearbox does most of your step-up for you, so you print far less. Low Kv = volts at low rpm. Best value for a slow machine.',
  },
  bldc: {
    label: 'BLDC / outrunner as alternator',
    Kv: 80, Ri: 0.25, phases: 3, drop: 0.6,
    note: 'True AC out, no brushes to drag at crawl speed, very low resistance. Needs a 3-phase Schottky bridge — budget 0.6 V for it.',
  },
  custom: { label: 'Custom — enter Kv and Ri yourself', Kv: null, Ri: null, phases: 1, drop: 0, note: '' },
};

/**
 * Output stages. `minVin` is the startup/dropout voltage — the single spec that
 * decides whether a slow build produces anything at all, rather than a little.
 */
const CONVERTERS = {
  buck: {
    label: 'Fixed buck module (typical 12 V)',
    minVin: 14, eff: 0.9, maxW: 100,
    note: 'Cheap and efficient, but it can only step DOWN. Below its dropout the output is zero, not small.',
  },
  buckboost: {
    label: 'Buck-boost / SEPIC',
    minVin: 3, eff: 0.88, maxW: 50,
    note: 'Works above OR below the target rail. The sensible default for a machine whose speed varies by 100x.',
  },
  harvester: {
    label: 'Energy harvester (LTC3105 / bq25504 class)',
    minVin: 0.25, eff: 0.7, maxW: 0.5,
    note: 'Starts from a quarter of a volt. Less efficient and low-current, but it is the only thing that turns a very slow rise into charge.',
  },
};

const DEFAULTS = {
  gas: 'helium',
  balloonCount: 1,
  balloonDiameterM: 3,
  payloadKg: 2,
  dragCd: 0.47, // sphere
  ropeLengthM: 20,
  drumDiameterMm: 120,
  gearRatio: 25, // generator turns per drum turn
  gearEffPct: 85,
  genType: 'gearmotor',
  genKv: 60, // rpm per volt
  genRi: 3.5, // coil resistance, ohms
  loadR: 8, // electrical load seen by the generator, ohms
  converter: 'buckboost',
  regVout: 12, // output rail you want
  crankDownSpeedMs: 1,
  sessionMinutes: 60,
  targetRunMinutes: 5, // how long you want one pull to last
  targetOutputW: 3, // "decent power" — a phone, a lamp, a radio
};

/* ------------------------------ physics ------------------------------ */

const sphereVolume = (d) => (4 / 3) * Math.PI * (d / 2) ** 3;
const sphereDiameter = (v) => 2 * Math.cbrt((3 * Math.max(v, 0)) / (4 * Math.PI));

function fmt(n, digits = 2) {
  if (!isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs !== 0 && abs < 0.01) return n.toExponential(2);
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

function mmss(seconds) {
  if (!isFinite(seconds) || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m} min ${s} s` : `${s} s`;
}

/**
 * Suggest a printable step-up gear train (drum slow -> generator fast).
 * Caps each stage at 7:1 (12T pinion, <=84T wheel — still printable) and adds
 * stages as needed, so any ratio maps to teeth you can actually print.
 */
function suggestGears(ratio) {
  const PINION_T = 12;
  const MAX_DRIVER = 84;
  if (ratio <= 1.01) return { stages: [{ driver: PINION_T, driven: PINION_T }], achieved: 1 };
  const perStageMax = MAX_DRIVER / PINION_T;
  const nStages = Math.max(1, Math.ceil(Math.log(ratio) / Math.log(perStageMax)));
  const driver = Math.min(MAX_DRIVER, Math.max(PINION_T, Math.round(PINION_T * ratio ** (1 / nStages))));
  return {
    stages: Array.from({ length: nStages }, () => ({ driver, driven: PINION_T })),
    achieved: (driver / PINION_T) ** nStages,
  };
}

/**
 * Steady-state operating point at a given gear ratio.
 *
 * Force balance on the rising balloon at terminal speed:
 *   net buoyancy F = aerodynamic drag (1/2 rho Cd A v^2) + generator load (b v)
 * The generator load is a DC machine reflected through the gears:
 *   b = ratio^2 K^2 / (r_drum^2 (Ri + Rload)),  motor constant K = 9.5493 / Kv
 * so k v^2 + b v - F = 0, solved for v. Current, torque and power follow.
 */
function solveOperatingPoint(ratio, c) {
  const k = 0.5 * RHO_AIR * c.Cd * c.area;
  const Rtot = Math.max(c.Ri + c.Rload, 1e-3);
  const b = (ratio * ratio * c.K * c.K) / (Math.max(c.drumRadius, 1e-4) ** 2 * Rtot);
  let v;
  if (c.Fnet <= 0) v = 0;
  else if (k < 1e-9) v = c.Fnet / Math.max(b, 1e-9);
  else v = (-b + Math.sqrt(b * b + 4 * k * c.Fnet)) / (2 * k);
  v = Math.max(v, 0);
  const omegaGen = (ratio * v) / Math.max(c.drumRadius, 1e-4);
  const I = (c.K * omegaGen) / Rtot;
  const Pmech = b * v * v; // = F_load * v
  return {
    v,
    omegaGen,
    I,
    Pmech,
    // Gear friction, then the coil's own Ri loss — only the Rload share is usable.
    Pelec: c.etaGear * Pmech * (c.Rload / Rtot),
    Vload: I * c.Rload,
    Fdrag: k * v * v,
    Fload: b * v,
  };
}

// Rise durations the ladder reports on, roughly log-spaced.
const RISE_TIMES = [10, 30, 60, 120, 300, 900, 1800, 3600];

/** The physical constants of a given design, shared by every solver below. */
function contextOf(inp) {
  const gas = GASES[inp.gas];
  const liftPerM3 = (RHO_AIR - gas.density) * G; // N per m^3
  const diameter = Math.max(inp.balloonDiameterM, 0.1);
  const count = Math.max(Math.round(inp.balloonCount || 1), 1);
  const volume = sphereVolume(diameter); // per balloon
  const totalVolume = volume * count;
  const grossN = liftPerM3 * totalVolume;
  const payloadN = Math.max(inp.payloadKg, 0) * G;
  return {
    gas, liftPerM3, diameter, count, volume, totalVolume, grossN, payloadN,
    Cd: Math.max(inp.dragCd, 0.05),
    // n separate spheres, so frontal area scales with n — not as one big sphere.
    area: count * Math.PI * (diameter / 2) ** 2,
    drumRadius: Math.max(inp.drumDiameterMm, 5) / 1000 / 2,
    K: 9.5493 / Math.max(inp.genKv, 1),
    Ri: Math.max(inp.genRi, 0.01),
    Rload: Math.max(inp.loadR, 0.1),
    etaGear: Math.min(Math.max(inp.gearEffPct, 1), 99) / 100,
    Fnet: grossN - payloadN,
  };
}

/**
 * The gear ratio that makes the rise last exactly `hours` — the inverse of
 * solveOperatingPoint, and exact rather than swept.
 *
 * At the target speed v the force balance must hold, so the generator has to
 * absorb whatever buoyancy drag does not:
 *   b = (F_net - 1/2 rho Cd A v^2) / v,   and   b = ratio^2 K^2 / (r^2 Rtot)
 * hence ratio = sqrt(b r^2 Rtot) / K.
 *
 * Returns null when the target is impossible: a rise that FAST would need the
 * generator to push rather than resist (drag alone already holds it slower).
 */
function ratioForRiseTime(seconds, inp) {
  const c = contextOf(inp);
  const v = inp.ropeLengthM / Math.max(seconds, 1e-3);
  const bNeeded = (c.Fnet - 0.5 * RHO_AIR * c.Cd * c.area * v * v) / v;
  if (!(bNeeded > 0)) return null;
  return Math.sqrt(bNeeded * c.drumRadius ** 2 * (c.Ri + c.Rload)) / c.K;
}

/** Every number the page shows, from one input object. */
function computeAll(inp) {
  const ctx = contextOf(inp);
  const { gas, liftPerM3, diameter, count, volume, totalVolume, grossN, payloadN } = ctx;
  const netN = ctx.Fnet;

  const drumRadius = ctx.drumRadius;
  const drumCircumferenceMm = Math.PI * Math.max(inp.drumDiameterMm, 5);
  const ratio = Math.max(inp.gearRatio, 0.1);

  const op = solveOperatingPoint(ratio, ctx);

  // Rope geometry — the question is "per 1 mm of rope", so work in mm.
  const drumTurnsPerMm = 1 / drumCircumferenceMm;
  const genTurnsPerMm = drumTurnsPerMm * ratio;
  const drumTurnsTotal = (inp.ropeLengthM * 1000) / drumCircumferenceMm;

  const drumRpm = ((op.v / drumRadius) * 60) / (2 * Math.PI);
  const genRpm = drumRpm * ratio;

  // Timing. The up-stroke is the only part that generates; the crank-down is
  // the reset, and it is dead time unless you also drive the generator on it.
  const tUp = op.v > 1e-4 ? inp.ropeLengthM / op.v : Infinity;
  const tDown = inp.ropeLengthM / Math.max(inp.crankDownSpeedMs, 0.01);
  const tCycle = tUp + tDown;
  const cyclesPerHour = isFinite(tCycle) && tCycle > 0 ? 3600 / tCycle : 0;
  const sessionS = Math.max(inp.sessionMinutes, 0) * 60;
  const cyclesInSession = isFinite(tCycle) && tCycle > 0 ? Math.floor(sessionS / tCycle) : 0;
  const pullingS = cyclesInSession * (isFinite(tUp) ? tUp : 0);
  const dutyPct = isFinite(tCycle) && tCycle > 0 ? (tUp / tCycle) * 100 : 0;
  const energyWh = (op.Pelec * pullingS) / 3600;

  // Regulator output stage. The generator's raw output swings with speed; the
  // regulator is what makes it a usable rail — but only while Vin clears its
  // minimum, which is the failure this stage exists to expose.
  const conv = CONVERTERS[inp.converter] || CONVERTERS.buckboost;
  const gen = GENERATORS[inp.genType] || GENERATORS.custom;
  const regEff = conv.eff;
  const regVout = Math.max(inp.regVout, 0.5);
  // A brushless machine pays its rectifier bridge before the converter ever
  // sees the voltage — and on a slow rise that drop is most of the output.
  const railV = Math.max(op.Vload - gen.drop, 0);
  const rectLossW = Math.min(gen.drop, op.Vload) * op.I;
  const regOk = railV >= conv.minVin;
  // Rated power is a real wall: a harvester IC will not pass 30 W however much
  // the rope offers it, and a page that promises otherwise is lying about parts
  // you can buy. Anything past the rating is spilled, not banked.
  const regRaw = regOk ? Math.max(op.Pelec - rectLossW, 0) * regEff : 0;
  const regPout = Math.min(regRaw, conv.maxW);
  const regCapped = regRaw > conv.maxW;
  const regIout = regPout / regVout;
  // Rope speed at which the generator EMF first reaches the converter's floor.
  const vForRegMin =
    ((conv.minVin + gen.drop) * (drumRadius * (ctx.Ri + ctx.Rload))) / (ctx.K * ratio * ctx.Rload);

  // ---- what you get, and for how long, across every plausible rise time ----
  // This is the page's headline answer: the stroke energy is fixed, so each row
  // is the SAME joules spent at a different rate — and the only thing that
  // varies is whether the output stage can still see it.
  const ladder = RISE_TIMES.map((secs) => {
    const rr = ratioForRiseTime(secs, inp);
    if (!rr) return { secs, impossible: true };
    const o = solveOperatingPoint(rr, ctx);
    const rv = Math.max(o.Vload - gen.drop, 0);
    const ok = rv >= conv.minVin;
    const raw = ok ? Math.max(o.Pelec - Math.min(gen.drop, o.Vload) * o.I, 0) * regEff : 0;
    const pOut = Math.min(raw, conv.maxW);
    return {
      secs, ratio: rr, P: o.Pelec, V: o.Vload, railV: rv, ok,
      pOut, capped: raw > conv.maxW,
      wh: (pOut * secs) / 3600, mA: (pOut / regVout) * 1000,
    };
  });

  // ---- the run-time budget -------------------------------------------------
  // Energy per up-stroke is force x distance and NOTHING else. Gearing changes
  // how long the stroke takes and how many watts come out, but the product of
  // the two is pinned. So "make it last longer" is not a gearing problem, it is a
  // net-lift-times-rope-length problem — this block says so in numbers.
  const etaChain = ctx.etaGear * (ctx.Rload / (ctx.Ri + ctx.Rload));
  const strokeJ = Math.max(netN, 0) * inp.ropeLengthM * etaChain;
  const targetS = Math.max(inp.targetRunMinutes, 0.05) * 60;
  const sustainedW = strokeJ / targetS; // what one stroke can hold for that long
  const targetW = Math.max(inp.targetOutputW, 0);

  // What the design would have to become to hold targetW for targetS.
  const neededJ = (targetW * targetS) / regEff;
  const neededNetN = neededJ / (Math.max(inp.ropeLengthM, 0.1) * etaChain);
  const neededVolume = (neededNetN + payloadN) / liftPerM3;
  const neededDiameter = sphereDiameter(neededVolume / count);
  const neededCount = Math.ceil(neededVolume / Math.max(volume, 1e-9));
  const neededRopeM = neededJ / (Math.max(netN, 1e-6) * etaChain); // the other lever

  // Gearing that actually delivers that rise time, and whether the rail survives it.
  const tunedRatio = ratioForRiseTime(targetS, inp);
  const tunedOp = tunedRatio ? solveOperatingPoint(tunedRatio, ctx) : null;

  return {
    gas, liftPerM3, diameter, count, volume, totalVolume, grossN, payloadN, netN,
    liftMassKg: grossN / G, netMassKg: netN / G, payloadKg: Math.max(inp.payloadKg, 0),
    drumCircumferenceMm, drumTurnsPerMm, genTurnsPerMm, drumTurnsTotal,
    ratio, gears: suggestGears(ratio), op, drumRpm, genRpm,
    tUp, tDown, tCycle, cyclesPerHour, cyclesInSession, pullingS, dutyPct, energyWh,
    regOk, regPout, regIout, regVout, regEff, regCapped, vForRegMin, railV, rectLossW,
    conv, gen, ladder,
    strokeJ, strokeWh: strokeJ / 3600, targetS, targetMin: targetS / 60, targetW,
    sustainedW, sustainedOutW: sustainedW * regEff,
    neededNetN, neededVolume, neededDiameter, neededCount, neededRopeM,
    tunedRatio, tunedOp,
    tunedRailV: tunedOp ? Math.max(tunedOp.Vload - gen.drop, 0) : 0,
    tunedRegOk: tunedOp ? tunedOp.Vload - gen.drop >= conv.minVin : false,
    feasible: netN > 0,
  };
}

const SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'Balloon Generator Calculator',
      description:
        'Free online course and calculator for a buoyancy-driven generator: balloon diameter and gas volume, lift force in newtons and kilograms, rope and drum turns, gear ratio, generator RPM, run time and regulated output.',
      url: `${SITE_URL}${TOOL_PATH}`,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Any',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Open Source', item: `${SITE_URL}/open-source` },
        { '@type': 'ListItem', position: 3, name: 'Balloon Generator', item: `${SITE_URL}${TOOL_PATH}` },
      ],
    },
  ],
};

/* ------------------------------ page ------------------------------ */

const BalloonGenerator = () => {
  const [inp, setInp] = useState(DEFAULTS);
  const r = useMemo(() => computeAll(inp), [inp]);
  const set = (patch) => setInp((prev) => ({ ...prev, ...patch }));

  return (
    <div className="rounded-xl bg-slate-100 p-1 sm:p-4 border border-gray-800">
      <SEO
        title="Balloon Generator — build course & calculator"
        description="Learn to build a buoyancy-powered generator, one lesson at a time, with a live calculator: balloon diameter and gas volume, lift force in newtons and kilograms, generator turns per millimetre of rope, gear ratio and RPM, run time per session, and the regulated output rail."
        keywords="balloon generator, buoyancy generator, helium lift calculator, newtons to kg lift, gear ratio calculator, off-grid generator, 3d printed generator"
        url={TOOL_PATH}
        type="website"
        schema={SCHEMA}
      />

      <div className="max-w-4xl mx-auto pb-12 px-3 sm:px-6">
        {/* ---------- hero ---------- */}
        <header className="pt-4 pb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            Open source · Build course
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
            The Balloon Generator
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            A balloon on a rope. It rises, it unwinds a drum, the drum spins a motor
            backwards, and the motor becomes a generator. Buoyancy does the work going
            up; a person cranks it back down to reset. Six lessons take you from an
            empty envelope to a regulated 12&nbsp;V rail — and the calculator beside
            each lesson gives you the real numbers for <em>your</em> parts.
          </p>
          <Figure src={IMG.hero} alt="Colourful lifting balloons tethered above a garden, powering a home" />
          <p className="mt-3 inline-block text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1">
            Runs entirely in your browser — works offline once loaded.
          </p>
        </header>

        {/* ---------- honesty note ---------- */}
        <Callout tone="amber" title="Read this before you build">
          This machine does not create energy. Buoyancy sets a hard ceiling on the
          force you have, and gearing only ever trades force for speed. The up-stroke
          is over in seconds, and every joule it gave you has to be put back in by hand
          to reset it — so read the duty cycle in Lesson 6, not the peak watts in Lesson
          4. It is a superb teaching machine and a real off-grid trickle charger. It is
          not a power plant.
        </Callout>

        {!r.feasible && (
          <Callout tone="rose" title="This balloon cannot lift its own payload">
            Gross lift is {fmt(r.grossN)}&nbsp;N but the payload needs {fmt(r.payloadN)}&nbsp;N.
            Increase the diameter or cut the payload before any other number means anything.
          </Callout>
        )}

        {/* ---------- lesson 1 ---------- */}
        <Lesson n={1} title="The balloon: volume, lift force, and what that means in kilograms">
          <p>
            Lift is displacement. A balloon pushes air out of the way, and the air pushes
            back with the weight of everything it displaced. So the only two things that
            matter are <strong>how big the envelope is</strong> and{' '}
            <strong>how much lighter than air the gas inside is</strong>.
          </p>
          <Formula>
            V = 4⁄3 · π · r³ &nbsp;·&nbsp; F<sub>gross</sub> = (ρ<sub>air</sub> − ρ<sub>gas</sub>) · g · V
          </Formula>
          <p>
            That gives you a force in newtons. Newtons are unintuitive, so convert:{' '}
            <strong>F = m · g</strong>, therefore <strong>m = F ÷ g</strong>. Divide any
            lift figure by 9.81 and you have the mass it will hold up. 500&nbsp;N of lift
            is 500 ÷ 9.81 = <strong>51&nbsp;kg</strong> — a person. That is the number to
            picture when you are deciding how big to go.
          </p>

          <Figure src={IMG.lift} alt="A single huge lifting balloon seen from below, two people on the meadow beneath it for scale" />

          <Inputs>
            <Choice
              label="Lifting gas"
              value={inp.gas}
              onChange={(gas) => set({ gas })}
              options={Object.entries(GASES).map(([k, g]) => ({ value: k, label: g.label }))}
            />
            <Num label="Balloon diameter (each)" unit="m" value={inp.balloonDiameterM} min={0.2} max={15} step={0.1}
                 onChange={(balloonDiameterM) => set({ balloonDiameterM })} />
            <Num label="How many balloons" unit="on one rope" value={inp.balloonCount} min={1} max={40} step={1}
                 onChange={(balloonCount) => set({ balloonCount })} />
            <Num label="Payload (rig, rope, drum)" unit="kg" value={inp.payloadKg} min={0} max={200} step={0.5}
                 onChange={(payloadKg) => set({ payloadKg })} />
          </Inputs>
          <p className={`text-xs ${r.gas.warn ? 'text-amber-700' : 'text-slate-500'}`}>{r.gas.note}</p>

          <Results>
            <Stat label="Gas volume" value={`${fmt(r.totalVolume)} m³`}
                  hint={r.count > 1 ? `${r.count} × ${fmt(r.volume)} m³ — the recurring cost` : 'sphere of that diameter'} />
            <Stat label="Gross lift force" value={`${fmt(r.grossN)} N`} hint={`${fmt(r.liftPerM3)} N per m³`} />
            <Stat label="Gross lift as mass" value={`${fmt(r.liftMassKg)} kg`} hint="F ÷ 9.81" accent />
            <Stat label="Net lift after payload" value={`${fmt(r.netN)} N`} hint={`= ${fmt(r.netMassKg)} kg spare`} accent />
          </Results>
          <Aside>
            Net lift is the whole budget for the rest of the machine. Everything from
            here — rope speed, RPM, watts — is spent out of these {fmt(r.netN)}&nbsp;N.
            {r.count > 1 && ' Adding balloons is the one change that grows this budget rather than just redistributing it, and it scales slightly better than linearly because the rope, drum and gearbox are payload you carry once however many balloons pull on them.'}
            {r.count > 1 && ` Resetting them all costs ${fmt(r.netMassKg, 0)} kg of pull, so plan the crank leverage or a geared winch.`}
          </Aside>
        </Lesson>

        {/* ---------- lesson 2 ---------- */}
        <Lesson n={2} title="The rope and the drum: turning metres into turns">
          <p>
            The drum converts a straight pull into rotation. One full turn of the drum
            takes in one circumference of rope, so a fat drum gives you high torque and
            few turns, and a thin drum gives you many turns and little torque. This is
            your first gear ratio, and it is free.
          </p>
          <Formula>
            circumference = π · D<sub>drum</sub> &nbsp;·&nbsp; drum turns per mm = 1 ⁄ circumference<sub>mm</sub>
          </Formula>
          <Figure src={IMG.drum} alt="Close detail of the rope drum and printed gear train" />

          <Inputs>
            <Num label="Rope length (working height)" unit="m" value={inp.ropeLengthM} min={1} max={300} step={1}
                 onChange={(ropeLengthM) => set({ ropeLengthM })} />
            <Num label="Drum diameter" unit="mm" value={inp.drumDiameterMm} min={10} max={600} step={5}
                 onChange={(drumDiameterMm) => set({ drumDiameterMm })} />
          </Inputs>

          <Results>
            <Stat label="Drum circumference" value={`${fmt(r.drumCircumferenceMm, 1)} mm`} hint="rope taken per drum turn" />
            <Stat label="Drum turns per 1 mm of rope" value={fmt(r.drumTurnsPerMm, 4)} hint="before gearing" />
            <Stat label="Generator turns per 1 mm of rope" value={fmt(r.genTurnsPerMm, 3)} hint={`at ${fmt(r.ratio, 1)}:1 gearing`} accent />
            <Stat label="Drum turns over the full rope" value={fmt(r.drumTurnsTotal, 0)} hint={`${fmt(inp.ropeLengthM)} m of rope`} />
          </Results>
          <Aside>
            Generator turns per millimetre is the number that decides whether your motor
            ever reaches a useful voltage. It is set here, in the drum and gears — not by
            the balloon.
          </Aside>
        </Lesson>

        {/* ---------- lesson 3 ---------- */}
        <Lesson n={3} title="The gearbox: how fast the generator actually spins">
          <p>
            A drum turning at walking pace is far too slow for a DC motor to make voltage.
            Step-up gearing fixes the speed — and charges you for it in force. Gear too
            hard and the load exceeds your net lift, the balloon slows to a crawl, and the
            output collapses. There is a peak, and the calculator will find it for you.
          </p>
          <Formula>
            RPM<sub>gen</sub> = RPM<sub>drum</sub> · ratio &nbsp;·&nbsp; V ≈ RPM<sub>gen</sub> ⁄ K<sub>v</sub>
          </Formula>

          <Inputs>
            <Num label="Gear ratio (generator : drum)" unit=":1" value={inp.gearRatio} min={1} max={2000} step={1}
                 onChange={(gearRatio) => set({ gearRatio })} />
            <Num label="Gear train efficiency" unit="%" value={inp.gearEffPct} min={30} max={98} step={1}
                 onChange={(gearEffPct) => set({ gearEffPct })} />
            <Num label="Drag coefficient (sphere ≈ 0.47)" unit="" value={inp.dragCd} min={0.1} max={1.2} step={0.01}
                 onChange={(dragCd) => set({ dragCd })} />
          </Inputs>
          {r.tunedRatio && (
            <button
              type="button"
              onClick={() => set({ gearRatio: Math.round(r.tunedRatio * 10) / 10 })}
              className="mt-3 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              Gear it for a {fmt(r.targetMin, 1)}-minute rise ({fmt(r.tunedRatio, 0)}:1)
            </button>
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            Set the target duration in Lesson 6. Peak watts is the wrong thing to chase
            here — see the energy budget there first.
          </p>

          <Results>
            <Stat label="Rise speed" value={`${fmt(r.op.v)} m/s`} hint="steady state, drag + generator load" />
            <Stat label="Drum speed" value={`${fmt(r.drumRpm, 1)} rpm`} />
            <Stat label="Generator speed" value={`${fmt(r.genRpm, 0)} rpm`} accent />
            <Stat label="Printable gear train" value={r.gears.stages.map((s) => `${s.driver}:${s.driven}`).join(' → ')}
                  hint={`${r.gears.stages.length} stage(s), ${fmt(r.gears.achieved, 1)}:1 achieved`} />
          </Results>
          <Aside>
            Of {fmt(r.netN)}&nbsp;N of net lift, {fmt(r.op.Fdrag)}&nbsp;N is lost to air drag
            and {fmt(r.op.Fload)}&nbsp;N is doing useful work against the generator.
          </Aside>
        </Lesson>

        {/* ---------- lesson 4 ---------- */}
        <Lesson n={4} title="The generator stage: from rotation to watts">
          <p>
            A brushed DC motor spun backwards is a generator. Its K<sub>v</sub> (rpm per
            volt) tells you the voltage it makes at a given speed; its coil resistance
            R<sub>i</sub> tells you how much of that you lose as heat before it ever
            reaches your load. Low K<sub>v</sub> means more volts at low speed — which is
            exactly what a slow machine like this wants.
          </p>
          <Formula>
            K = 9.5493 ⁄ K<sub>v</sub> &nbsp;·&nbsp; I = K·ω ⁄ (R<sub>i</sub> + R<sub>load</sub>) &nbsp;·&nbsp; P = I² · R<sub>load</sub>
          </Formula>

          <p>
            Which machine you buy decides more than any printed part. A brushed motor is
            cheap and needs no rectifier, but its brushes drag at crawl speed. A brushless
            outrunner run as an alternator makes real AC, has almost no resistance and
            nothing to wear — at the cost of a 3-phase bridge, and{' '}
            <strong>two diodes are always in series across that bridge</strong>. Use
            Schottky and you pay ~0.6&nbsp;V; use ordinary silicon and you pay ~1.4&nbsp;V,
            which on a slow rise is most of everything you made.
          </p>

          <Inputs>
            <Choice
              label="Generator"
              value={inp.genType}
              onChange={(genType) => {
                const g = GENERATORS[genType];
                set(g.Kv ? { genType, genKv: g.Kv, genRi: g.Ri } : { genType });
              }}
              options={Object.entries(GENERATORS).map(([k, g]) => ({ value: k, label: g.label }))}
            />
            <Num label="Generator Kv" unit="rpm/V" value={inp.genKv} min={5} max={3000} step={5}
                 onChange={(genKv) => set({ genKv, genType: 'custom' })} />
            <Num label="Coil resistance Ri" unit="Ω" value={inp.genRi} min={0.05} max={50} step={0.05}
                 onChange={(genRi) => set({ genRi, genType: 'custom' })} />
          </Inputs>
          {r.gen.note && <p className="text-xs text-slate-500">{r.gen.note}</p>}
          <Inputs>
            <Num label="Load resistance" unit="Ω" value={inp.loadR} min={0.5} max={500} step={0.5}
                 onChange={(loadR) => set({ loadR })} />
          </Inputs>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">The force balance — this is the whole machine</p>
            <p className="mt-1 text-[13px] text-slate-600">
              The balloon speeds up until the generator resists exactly as hard as buoyancy
              pulls. That balance point <em>is</em> the operating point, and it is what the
              calculator solves for. Gear ratio does not change the balance — it changes
              which speed the balance happens at.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Net lift pulling up" value={`${fmt(r.netN)} N`} />
              <Stat label="Air drag resisting" value={`${fmt(r.op.Fdrag)} N`} />
              <Stat label="Generator resisting" value={`${fmt(r.op.Fload)} N`}
                    hint={`${fmt((r.op.Fload * (r.drumCircumferenceMm / 2000 / Math.PI)) / r.ratio, 5)} Nm at the generator shaft`} accent />
              <Stat label="Balance" value={`${fmt(r.op.Fdrag + r.op.Fload)} N`} hint="equals net lift, by definition" accent />
            </div>
          </div>

          <Results>
            <Stat label="Generator voltage" value={`${fmt(r.op.Vload)} V`} hint="across the load" accent />
            <Stat label="Current" value={`${fmt(r.op.I)} A`} />
            <Stat label="Mechanical power in" value={`${fmt(r.op.Pmech)} W`} hint="what the rope delivers" />
            <Stat label="Electrical power out" value={`${fmt(r.op.Pelec)} W`} hint="after gear + coil losses" accent />
          </Results>
        </Lesson>

        {/* ---------- lesson 5 ---------- */}
        <Lesson n={5} title="The output stage: choosing the converter that decides everything">
          <p>
            Raw generator output rises and falls with rope speed — useless for charging
            anything directly. A buck (or buck-boost) regulator flattens it into a fixed
            rail. The catch is the <strong>dropout</strong>: the regulator needs a minimum
            input voltage to hold its output, and below that the rail simply stops. That
            threshold, not the average power, is what usually kills a first build.
          </p>
          <Figure src={IMG.schematic} alt="Schematic of the balloon generator: balloon, rope, drum, gearbox, generator, regulator, battery" />

          <Callout tone="emerald" title="The counter-intuitive one: Kv barely matters, the LOAD sets your voltage">
            At the balance point the current is fixed by power alone —
            I = √(F·v ⁄ R<sub>total</sub>) — so the voltage is
            V = R<sub>load</sub> · √(F·v ⁄ R<sub>total</sub>). Change the generator's
            K<sub>v</sub> and the gear ratio that holds your chosen rise time moves by
            exactly the same factor, leaving the voltage untouched: a K<sub>v</sub> 20,
            80 or 400 machine all land on the same volts and the same watts. What does
            move it is the <strong>load resistance</strong>. On this design, 8 Ω gives
            3.3 V while 200 Ω gives 16.7 V — at the same power. If your rail is dropping
            out, raise the load impedance before you buy another motor.
          </Callout>

          <Callout tone="amber" title="A transformer will not help here">
            It is the obvious idea and it fails twice. A transformer needs AC, so on a
            brushed machine it does nothing at all. And even on an alternator it cannot
            make power — it trades volts for amps at constant watts. What you actually
            need at low speed is the opposite of stepping down: a <strong>boost</strong>{' '}
            converter, which is a transformer's job done on DC by switching, and which
            does not care that your frequency swings by 100× as the balloon slows.
          </Callout>

          <Figure src={IMG.bench} alt="The converter, rectifier bridge and battery pack wired up on a workshop bench" />

          <Inputs>
            <Choice
              label="Output stage"
              value={inp.converter}
              onChange={(converter) => set({ converter })}
              options={Object.entries(CONVERTERS).map(([k, c]) => ({ value: k, label: c.label }))}
            />
            <Num label="Output rail you want" unit="V" value={inp.regVout} min={3} max={60} step={0.5}
                 onChange={(regVout) => set({ regVout })} />
          </Inputs>
          <p className="text-xs text-slate-500">{r.conv.note}</p>

          <Results>
            <Stat label="Generator makes" value={`${fmt(r.op.Vload)} V`}
                  hint={r.gen.drop ? `less ${fmt(r.gen.drop)} V rectifier → ${fmt(r.railV)} V` : 'straight to the converter'} />
            <Stat label="Converter needs" value={`${fmt(r.conv.minVin)} V`}
                  hint={r.regOk ? 'cleared' : 'NOT cleared — output is zero'} accent={r.regOk} />
            <Stat label="Regulated output" value={r.regOk ? `${fmt(r.regPout)} W` : 'no output'}
                  hint={`at ${fmt(r.regVout)} V, ${fmt(r.regEff * 100, 0)}% efficient`} accent={r.regOk} />
            <Stat label="Output current" value={r.regOk ? `${fmt(r.regIout * 1000, 0)} mA` : '—'}
                  hint={r.regCapped ? `capped at the ${fmt(r.conv.maxW)} W rating — the rest is spilled` : undefined} />
          </Results>
          {!r.regOk && (
            <Callout tone="rose" title="The rail will not hold">
              The converter sees {fmt(r.railV)}&nbsp;V and needs {fmt(r.conv.minVin)}&nbsp;V,
              so it produces nothing — not a little, nothing. Pick a lower-startup output
              stage above, drop to a lower-K<sub>v</sub> generator, or gear harder.
            </Callout>
          )}

          {/* The page's headline answer: same joules, spent at different rates. */}
          <div className="pt-2">
            <h3 className="text-sm font-semibold text-slate-900">
              What you get, and for how long
            </h3>
            <p className="mt-1 text-[13px] text-slate-500">
              Every row is the same {fmt(r.strokeWh, 3)}&nbsp;Wh stroke, geared to be spent
              at a different rate. Note how little the watt-hours change until the output
              stage stops seeing enough voltage — and then how completely they vanish.
            </p>
            <Ladder r={r} />
            <p className="mt-2 text-[11px] text-slate-400">
              Assumes your current balloon, rope, drum and generator. Change the output
              stage above and watch the bottom rows switch on.
            </p>
          </div>
        </Lesson>

        {/* ---------- lesson 6 ---------- */}
        <Lesson n={6} title="Run time: choosing how long one pull lasts">
          <p>
            Here is the fact the rest of the internet leaves out. The energy in one
            up-stroke is <strong>force × distance</strong> and nothing else. Gearing does
            not change it — gear harder and the balloon rises slower and makes fewer
            watts for longer; gear softer and it makes more watts for less time. The
            product is pinned. So gearing is how you <em>spend</em> the stroke; if you
            want a bigger stroke, that is a net-lift × rope-length question, and rope is
            the cheap lever.
          </p>
          <Formula>
            E<sub>stroke</sub> = F<sub>net</sub> · rope · η &nbsp;(fixed) &nbsp;·&nbsp; P<sub>sustained</sub> = E<sub>stroke</sub> ⁄ t<sub>target</sub>
          </Formula>

          <Results>
            <Stat label="Energy in one up-stroke" value={`${fmt(r.strokeWh, 3)} Wh`}
                  hint={`${fmt(r.strokeJ, 0)} J — unchanged by any gear ratio`} accent />
            <Stat label="Spread over your target" value={`${fmt(r.sustainedW, 3)} W`}
                  hint={`held for ${fmt(r.targetMin, 1)} min`} />
            <Stat label="After the regulator" value={`${fmt(r.sustainedOutW, 3)} W`}
                  hint={`at ${fmt(r.regEff * 100, 0)}% efficiency`} />
            <Stat label="You asked for" value={`${fmt(r.targetW, 1)} W`}
                  hint={r.sustainedOutW >= r.targetW ? 'the design covers it' : 'the design falls short'}
                  accent={r.sustainedOutW >= r.targetW} />
          </Results>

          <Figure src={IMG.reset} alt="Someone cranking the balloon back down at dusk, rope winding onto the drum" />

          <Inputs>
            <Num label="Target rise duration" unit="min" value={inp.targetRunMinutes} min={0.1} max={120} step={0.5}
                 onChange={(targetRunMinutes) => set({ targetRunMinutes })} />
            <Num label="Useful output you want" unit="W" value={inp.targetOutputW} min={0} max={500} step={0.5}
                 onChange={(targetOutputW) => set({ targetOutputW })} />
            <Num label="Crank-down speed" unit="m/s" value={inp.crankDownSpeedMs} min={0.05} max={5} step={0.05}
                 onChange={(crankDownSpeedMs) => set({ crankDownSpeedMs })} />
          </Inputs>

          {/* The whole point of the lesson: what it would take to actually get there. */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <p className="text-sm font-semibold text-emerald-900">
              To hold {fmt(r.targetW, 1)} W for {fmt(r.targetMin, 1)} minutes on one pull
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Net lift needed" value={`${fmt(r.neededNetN, 0)} N`} hint={`= ${fmt(r.neededNetN / G, 0)} kg`} />
              <Stat label="Balloon diameter needed" value={`${fmt(r.neededDiameter)} m`}
                    hint={`each, across ${r.count} balloon${r.count > 1 ? 's' : ''} · you have ${fmt(r.diameter)} m`}
                    accent={r.neededDiameter <= r.diameter} />
              <Stat label="…or this many of yours" value={`${fmt(r.neededCount, 0)}`}
                    hint={`${fmt(r.diameter)} m balloons · you have ${r.count}`}
                    accent={r.neededCount <= r.count} />
              <Stat label="…or rope this long instead" value={`${fmt(r.neededRopeM, 0)} m`}
                    hint={`at your current ${fmt(r.diameter)} m balloon · you have ${fmt(inp.ropeLengthM, 0)} m`}
                    accent={r.neededRopeM <= inp.ropeLengthM} />
              <Stat label="Gearing for that rise time"
                    value={r.tunedRatio ? `${fmt(r.tunedRatio, 0)}:1` : 'impossible'}
                    hint={r.tunedRatio
                      ? (r.tunedRegOk
                          ? `rail holds at ${fmt(r.tunedOp.Vload)} V`
                          : `rail drops out — only ${fmt(r.tunedOp.Vload)} V`)
                      : 'that rise is faster than free drag allows'}
                    accent={r.tunedRegOk} />
            </div>
            <p className="mt-3 text-[13px] text-emerald-900/80">
              Two levers, and they are interchangeable: a bigger balloon or a longer rope.
              Height is usually the cheap one — doubling the rope doubles the energy per
              stroke for the price of rope, while doubling the lift means roughly 1.26×
              the diameter and eight times the gas volume.
            </p>
          </div>

          {r.tunedRatio && !r.tunedRegOk && (
            <Callout tone="amber" title="The slow-rise trap">
              Gearing for {fmt(r.targetMin, 1)} minutes drops the generator to{' '}
              {fmt(r.tunedRailV)} V at the converter, below the {fmt(r.conv.minVin)} V it
              needs — so it would produce nothing at all. Generator voltage falls roughly
              with the square root of rope speed, so slower always means lower volts:
              stretch the rise far enough and every design hits this wall. Fix it with a
              lower-K<sub>v</sub> generator or a lower-startup converter (Lesson 5).
            </Callout>
          )}

          <p className="pt-2">
            The numbers below are the other mode — short strokes, cranked by hand, many
            times an hour. Only the up-stroke generates, so the duty cycle is what turns
            watts into watt-hours.
          </p>
          <Inputs>
            <Num label="Session length" unit="min" value={inp.sessionMinutes} min={1} max={720} step={5}
                 onChange={(sessionMinutes) => set({ sessionMinutes })} />
          </Inputs>

          <Results>
            <Stat label="One up-stroke" value={mmss(r.tUp)} hint={`${fmt(inp.ropeLengthM)} m at ${fmt(r.op.v)} m/s`} accent />
            <Stat label="Crank back down" value={mmss(r.tDown)} />
            <Stat label="Full cycle" value={mmss(r.tCycle)} hint={`${fmt(r.cyclesPerHour, 1)} cycles per hour`} />
            <Stat label="Duty cycle" value={`${fmt(r.dutyPct, 0)} %`} hint="share of time actually generating" />
          </Results>
          <Results>
            <Stat label={`Cycles in ${fmt(inp.sessionMinutes, 0)} min`} value={fmt(r.cyclesInSession, 0)} />
            <Stat label="Total time pulling" value={mmss(r.pullingS)} accent />
            <Stat label="Energy for the session" value={`${fmt(r.energyWh, 2)} Wh`} hint={r.regOk ? `${fmt(r.energyWh * r.regEff, 2)} Wh after the converter` : 'converter dropped out'} accent />
            <Stat label="Average power over the session" value={`${fmt((r.energyWh * 3600) / Math.max(inp.sessionMinutes * 60, 1))} W`} />
          </Results>
          <Aside>
            A one-hour session on the default 3&nbsp;m balloon spends most of its time being
            cranked, not generating. Longer rope is the cheapest fix: it lengthens the
            up-stroke without changing any of the physics above it.
          </Aside>
        </Lesson>

        {/* ---------- close ---------- */}
        <section className="mt-12 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Where this actually fits</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            One balloon trickle-charges a battery. A cluster of them on a shared drum,
            reset on a slow winch instead of by hand, becomes a genuinely useful garden
            installation — silent, no fuel, nothing to burn, and every part of it either
            printable or bought once. It will not run your house. It will run your lights,
            your radio and your phones, and it will teach anyone who helps you build it
            exactly where energy comes from.
          </p>
          <Figure src={IMG.house} alt="A finished balloon generator installation on a home, balloons tethered above a green roof" />
          <Figure src={IMG.cluster} alt="A community-scale installation: eight tethered balloons feeding a shared machine house" />
        </section>
      </div>
    </div>
  );
};

/* ------------------------------ ui bits ------------------------------ */

function Lesson({ n, title, children }) {
  return (
    <section className="mt-10 rounded-xl border border-slate-200 bg-white p-5 sm:p-7 shadow-sm">
      <div className="flex items-baseline gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
          {n}
        </span>
        <h2 className="text-lg sm:text-xl font-semibold text-slate-900 leading-snug">{title}</h2>
      </div>
      <div className="mt-4 space-y-4 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

/** Coarse duration label for the ladder — "10 s", "5 min", "6 h". */
function dur(s) {
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

/**
 * The same stroke energy spent at eight different rates. Rows that the output
 * stage cannot use are struck through rather than hidden — the point of the
 * table is seeing exactly where the cliff is.
 */
function Ladder({ r }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full min-w-[560px] text-left text-[13px]">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Rise lasts</th>
            <th className="px-3 py-2 font-medium">Gearing</th>
            <th className="px-3 py-2 font-medium">Generator</th>
            <th className="px-3 py-2 font-medium">Output</th>
            <th className="px-3 py-2 font-medium">Energy banked</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {r.ladder.map((row) => {
            if (row.impossible) {
              return (
                <tr key={row.secs} className="text-slate-400">
                  <td className="px-3 py-2">{dur(row.secs)}</td>
                  <td className="px-3 py-2" colSpan={4}>
                    impossible — drag alone already holds it slower than this
                  </td>
                </tr>
              );
            }
            return (
              <tr key={row.secs} className={row.ok ? 'text-slate-700' : 'text-slate-400'}>
                <td className="px-3 py-2 font-medium">{dur(row.secs)}</td>
                <td className="px-3 py-2 tabular-nums">{fmt(row.ratio, 0)}:1</td>
                <td className="px-3 py-2 tabular-nums">
                  {fmt(row.P, 2)} W @ {fmt(row.V, 2)} V
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {row.ok ? (
                    <span className={row.capped ? 'font-semibold text-amber-700' : 'font-semibold text-emerald-700'}>
                      {fmt(row.pOut, 2)} W · {fmt(row.mA, 0)} mA
                      {row.capped && <span className="font-normal"> (at its limit)</span>}
                    </span>
                  ) : (
                    <span title={`needs ${fmt(r.conv.minVin)} V, has ${fmt(row.railV, 2)} V`}>
                      dropped out
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{row.ok ? `${fmt(row.wh, 3)} Wh` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Formula({ children }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 font-mono text-[13px] text-slate-800 overflow-x-auto">
      {children}
    </div>
  );
}

function Inputs({ children }) {
  return <div className="grid gap-4 sm:grid-cols-3 pt-2">{children}</div>;
}

function Results({ children }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 pt-2">{children}</div>;
}

function Aside({ children }) {
  return (
    <p className="border-l-2 border-emerald-300 pl-3 text-[13px] text-slate-500 italic">{children}</p>
  );
}

function Callout({ tone = 'amber', title, children }) {
  const tones = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <div className={`mt-6 rounded-xl border p-4 text-sm leading-relaxed ${tones[tone]}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{children}</p>
    </div>
  );
}

/** Renders nothing if the asset has not been generated yet. */
function Figure({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="mt-5">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="w-full rounded-xl border border-slate-200 shadow-sm"
      />
      <figcaption className="mt-2 text-[11px] text-slate-400">{alt}</figcaption>
    </figure>
  );
}

function Num({ label, unit, value, onChange, min, max, step }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {label} {unit && <span className="text-slate-400">({unit})</span>}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          onChange(isNaN(v) ? 0 : v);
        }}
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
      />
    </label>
  );
}

function Choice({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Stat({ label, value, hint, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${accent ? 'text-emerald-800' : 'text-slate-900'}`}>{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

export default BalloonGenerator;
